import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Link, Typography } from '@mui/material';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { canViewProductLinks } from '../../config/permissions';
import { Rack } from './merchandisingTypes';
import { readNokeError } from './restockPlan';
import { buildDailySourceLookup, openDailySourceBatch, primaryDailyLinks, readDailySourceLinks, SourceLookup } from './dailySourcing';

export interface DailySourcing {
  identityKey: string;
  allowed: boolean;
  status: 'hidden' | 'loading' | 'ready' | 'error';
  links: Record<string, string[]>;
  error: string;
  retry: () => void;
}
type SourceState = Omit<DailySourcing, 'retry'>;
const emptyState = (identityKey: string): SourceState => ({ identityKey, allowed: false, status: 'hidden', links: {}, error: '' });

/** One capability check and one stored-link lookup for the entire daily page. */
export function useDailySourcing(racks: Rack[], enabled = true): DailySourcing {
  const { currentUser } = useAuth();
  const eligible = enabled && canViewProductLinks(currentUser?.id) && currentUser?.role !== 'counter';
  const requestKey = JSON.stringify(buildDailySourceLookup(racks));
  const identityKey = `${currentUser?.id ?? ''}:${currentUser?.role ?? ''}:${requestKey}`;
  const [revision, setRevision] = useState(0);
  const [state, setState] = useState<SourceState>(() => emptyState(identityKey));
  const retry = useCallback(() => {
    setState(emptyState(identityKey));
    setRevision((value) => value + 1);
  }, [identityKey]);
  useEffect(() => {
    let active = true;
    setState(emptyState(identityKey));
    if (!eligible) return () => { active = false; };
    const lookup: SourceLookup = JSON.parse(requestKey);
    (async () => {
      try {
        const capability = await api.get('/api/noke/merchandising/capabilities');
        if (!active || capability.data?.sourcing_links !== true) return;
        if (lookup.error) {
          setState({ ...emptyState(identityKey), allowed: true, status: 'error', error: lookup.error }); return;
        }
        setState({ ...emptyState(identityKey), allowed: true, status: 'loading' });
        if (!lookup.racks.length) {
          setState({ ...emptyState(identityKey), allowed: true, status: 'ready' }); return;
        }
        const { data } = await api.post('/api/noke/merchandising/sourcing-links', { racks: lookup.racks });
        if (!active) return;
        if (data?.allowed !== true) { setState(emptyState(identityKey)); return; }
        setState({ identityKey, allowed: true, status: 'ready', links: readDailySourceLinks(data, lookup.racks), error: '' });
      } catch (err) {
        if (!active) return;
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status === 401 || status === 403) { setState(emptyState(identityKey)); return; }
        setState({ ...emptyState(identityKey), status: 'error', error: readNokeError(err, '無法取得 Sinsang 連結，請重試。') });
      }
    })();
    return () => { active = false; };
  }, [eligible, identityKey, requestKey, revision]);
  // Returning from another tab rechecks permission before exposing cached source URLs.
  useEffect(() => {
    if (!eligible) return;
    window.addEventListener('focus', retry);
    return () => window.removeEventListener('focus', retry);
  }, [eligible, retry]);
  return { ...(eligible && state.identityKey === identityKey ? state : emptyState(identityKey)), retry };
}

export const DailySourcingToolbar: React.FC<{ sourcing: DailySourcing; skus: string[]; label?: string; description?: string }> = ({ sourcing, skus, label = '批次開啟 Sinsang',
  description = '只開本區待核對採購商品的主要連結，每次最多 10 個；不會下單或標記已訂。商品卡可另開備選連結。' }) => {
  const [progress, setProgress] = useState<{ identityKey: string; opened: string[]; blocked: boolean }>({ identityKey: '', opened: [], blocked: false });
  const selection = Array.from(new Set(skus));
  const links = primaryDailyLinks(selection, sourcing.links);
  const progressKey = `${sourcing.identityKey}:${selection.slice().sort().join(',')}`;
  const opened = progress.identityKey === progressKey ? progress.opened.filter((url) => links.includes(url)) : [];
  const remaining = links.filter((url) => !opened.includes(url));
  const missing = selection.filter((sku) => !sourcing.links[sku]?.length).length;
  if (!selection.length || sourcing.status === 'hidden') return null;
  if (sourcing.status === 'error') return <Alert severity="warning" action={<Button onClick={sourcing.retry}>重試貨源連結</Button>}>{sourcing.error}</Alert>;
  if (!sourcing.allowed) return null;
  if (sourcing.status === 'loading') return <Typography role="status" variant="body2">正在取得 Sinsang 連結…</Typography>;
  const openBatch = () => {
    const result = openDailySourceBatch(remaining, () => window.open('about:blank', '_blank'));
    setProgress({ identityKey: progressKey, opened: [...opened, ...result.opened], blocked: result.blocked });
  };
  return <Box sx={{ mb: 2, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
      <Button variant="contained" disabled={!remaining.length} onClick={openBatch}>
        {links.length ? (remaining.length ? `${label} · ${Math.min(10, remaining.length)} 個連結` : '本批連結已開啟') : '尚無可用的 Sinsang 連結'}
      </Button>
      <Typography variant="body2">{links.length} 個可用連結{opened.length > 0 ? ` · 已開啟 ${opened.length}／${links.length}` : ''}{missing > 0 ? ` · ${missing} 款尚無連結` : ''}</Typography>
    </Box>
    <Typography variant="caption" color="text.secondary">{description}</Typography>
    {progress.identityKey === progressKey && progress.blocked && <Alert severity="warning" sx={{ mt: 1 }}>瀏覽器阻擋了分頁；未開啟的連結仍保留。允許彈出式視窗後再開下一批，或逐一點商品卡連結。</Alert>}
  </Box>;
};

export const DailySourcingItem: React.FC<{ sourcing: DailySourcing; sku: string; primaryOnly?: boolean; label?: string }> = ({ sourcing, sku, primaryOnly = false, label }) => {
  if (!sourcing.allowed || sourcing.status === 'hidden') return null;
  if (sourcing.status === 'loading') return <Typography variant="caption" color="text.secondary">貨源連結載入中…</Typography>;
  if (sourcing.status !== 'ready' || !Object.prototype.hasOwnProperty.call(sourcing.links, sku)) return null;
  const links = primaryOnly ? sourcing.links[sku].slice(0, 1) : sourcing.links[sku];
  if (!links.length) return <Typography variant="caption" color="text.secondary">尚無 Sinsang 連結 · 請核對貨源</Typography>;
  return <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
    {links.map((url, index) => <Link key={url} href={url} target="_blank" rel="noopener noreferrer" aria-label={`${sku} ${index ? `Sinsang 備選連結 ${index}` : label ?? 'Sinsang 商品頁'}`} sx={{ fontSize: 13, fontWeight: 600 }}>
      {index ? `備選連結 ${index}` : label ?? 'Sinsang 商品頁 ↗'}
    </Link>)}
  </Box>;
};
